/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Tag.js
 *
 * Purpose: Renders the tag-list input used by FormV2 for TAG field types, including the
 *     email-list variant.
 *
 * Scope: FormV2 input rendering for tag and email-list entity fields.
 *
 * Tags: customer-portal
 *
 * Overview: Exports Tag, a memoized component that wraps MultiInput and binds its chip list to
 *     react-hook-form via useController. For the email_to field name it filters the entered
 *     chips through an email regex, only committing valid addresses via onChange and setting a
 *     custom field error (through the passed-in setError) when any invalid address is
 *     present; other tag fields commit all entered chips unchanged. The attached `transform`
 *     strategy converts between the server's string/array tag representation and MultiInput's
 *     `{ value }` object list, with a special `tag_values` payload key for the plain `tags`
 *     field name.
 *
 * Exports: default Tag component (with an attached `transform` static property).
 */

import React, { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { TagFieldNames } from 'shared/constants/fields';
import { isNonArrayObject } from 'shared/utils/functions';
import styled from 'styled-components';
import MultiInput from './MultiInput';

/**
 * @type {import('./types').FieldTransformStrategy}
 */
const transform = {
  fromServer(value) {
    return typeof value === 'string' ? value : value?.map((t) => t.value);
  },
  toServer(value, { field }) {
    const { name } = field;
    const tags = (Array.isArray(value) ? value : value.split(','))
      .map((v) => (isNonArrayObject(v) && 'value' in v ? v.value : v))
      .map((str) => str.trim())
      .filter(Boolean);
    return { [name === TagFieldNames.TAGS ? 'tag_values' : name]: tags };
  },
  defaultValue(value) {
    /**
     * Tags come in like this ['one', 'two'] OR 'one,two',
     * while <MultiInput> expects [{ value: 'One' }, { value: 'Two' }].
     */
    const tags = typeof value === 'string' ? value.split(',') : value;
    return tags.map((t) => ({ value: t })).filter((t) => t.value.trim());
  },
};

const Tag = React.memo(function Tag({
  name,
  control,
  required,
  displayName,
  readOnly,
  setError,
}) {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control, rules: { required } });

  const validateInput = useCallback(
    (inputs = []) => {
      if (name === TagFieldNames.EMAIL_TO) {
        const regex = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i);
        const invalidEmails = inputs.filter((email) => !email.match(regex));
        const validEmails = inputs.filter((email) => email.match(regex));

        if (invalidEmails.length) {
          if (!error) setError(`Invalid email address found`);
        } else {
          setError('');
        }

        onChange(validEmails);
      } else {
        onChange(inputs);
      }
    },
    [name, error, setError, onChange]
  );

  return (
    <div className="position-relative">
      <MultiInput
        placeholder={displayName}
        defaultValue={value}
        isDisabled={readOnly}
        onChange={validateInput}
      />
      {error && (
        <FieldError className="position-absolute fs-7 left-0 text-danger">
          {error.message}
        </FieldError>
      )}
    </div>
  );
});

const FieldError = styled.div`
  bottom: -1.35rem;
`;

Tag.transform = transform;
export default Tag;
